import React, { useEffect, useRef, useState } from "react";
import DataTable from "../Shared/DataTable/DataTable.js";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import TaskMenu, { EntityTypeIcon } from "./TaskMenu.js";
import { BoardView, BoardConfiguration } from "./Board.js";
import { invalidateTaskBoardsCache, loadTaskBoards } from "./taskBoardsCache.js";

const isTrue = (value) => value === true || value === "true" || value === "True";

const Breadcrumbs = ({ breadcrumbs, whereAmI, linkedEntityId, onSelect, hideAllTasksNav = false }) => {
    const crumbs = breadcrumbs || [];
    return (
        <div className="breadcrumbs">
            {crumbs.map((item, index) => (
                <span key={`${item.Name}-${index}`}>
                    {index > 0 && <span> &gt; </span>}
                    <span
                        className="breadcrumb"
                        onClick={() => {
                            if (index === 0 && !hideAllTasksNav)
                                onSelect(null, null, "All Tasks");
                            else
                                onSelect(item.EntityType, item.EntityId, item.Name);
                        }}
                    >
                        {item.Name}
                    </span>
                </span>
            ))}

            {crumbs.length === 1 && !hideAllTasksNav && (
                <span>
                    <span> &gt; </span>
                    <span className="breadcrumb" onClick={() => onSelect(null, null, "All Tasks")}>
                        All Tasks
                    </span>
                </span>
            )}

            {linkedEntityId != null && linkedEntityId !== "" && (
                <span>
                    <span> &gt; </span>
                    <span className="breadcrumb">{whereAmI}</span>
                </span>
            )}
        </div>
    );
};

const CountSummaries = ({ summary, isLoading }) => {
    const block = (title, icon, data) => (
        <div className="task-summary-column">
            <div className="task-summary-block">
                {isLoading || !data ? <LoadingSpinnerImage /> : (
                    <>
                        <div className="task-summary-header">
                            <i className={icon} style={{ color: "#3276b4", marginRight: "5px" }}></i> {title}:
                            <span style={{ fontSize: "21px", color: "#3276b4", marginLeft: "5px" }}>{data.total}</span>
                        </div>
                        <div className="details">
                            <span className="detail">
                                <i className="fas fa-circle late"></i>
                                <span><b>{data.late ?? 0}</b> Late</span>
                            </span>
                            <span className="detail">
                                <i className="fas fa-circle today"></i>
                                <span><b>{data.today ?? 0}</b> Today</span>
                            </span>
                            <span className="final-detail">
                                <i className="fas fa-circle next7days"></i>
                                <span><b>{data.next7days ?? 0}</b> Next 7 days</span>
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <div className="task-summary">
            {block("My Tasks", "fas fa-user", summary?.me)}
            {block("My Organization Tasks", "fas fa-users", summary?.company)}
        </div>
    );
};

const TaskManagerShell = ({
    baseStructure,
    ItemDetailsPane,
    AddItemPopup,
    ReloadStructureFunction,
    emitter,
    paramEntityType,
    paramEntityId,
    paramIsSlimVersion,
    paramExternalLinkedObjects,
    paramHasLimitedWidth,
    paramShowAllTasks,
    paramIsReviewApproved,
    paramShowHeaderlessCountSummaries,
}) => {
    const apiRef = useRef({});
    const isSlim = isTrue(paramIsSlimVersion) || isTrue(window.isSlimVersion);
    const fillParent = isTrue(paramHasLimitedWidth) || isSlim;
    const showLeftMenu = !isSlim && window.showLeftMenu !== false;
    // Slim embeds (appraisal card, item panes) hide summaries unless explicitly opted in.
    // Do not read window.showHeaderlessCountSummaries here — it leaks across page embeds.
    const showSummaries = !isSlim || isTrue(paramShowHeaderlessCountSummaries);
    // Prefer explicit props so embeds (appraisal card) don't overwrite the host page's entity type
    const initialEntityType = (paramEntityType != null && paramEntityType !== "") ? paramEntityType : (window.currentEntityType || "");
    const initialEntityId = (paramEntityId != null && paramEntityId !== "") ? paramEntityId : (window.currentEntityId || "");
    // Hide the "All Tasks" heading when embedded in another page / scoped to an entity
    const showWhereAmI = !isSlim
        && window.showWhereAmI !== false
        && !initialEntityType
        && window.showLeftMenu !== false;

    const showBreadcrumbs = !isSlim && window.showBreadcrumbs !== false;
    const [linkedEntityType, setLinkedEntityType] = useState(initialEntityType);
    const [linkedEntityId, setLinkedEntityId] = useState(initialEntityId);
    const [whereAmI, setWhereAmI] = useState("All Tasks");
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [menu, setMenu] = useState([]);
    const [fullMenu, setFullMenu] = useState(null);
    const [isLoadingMenu, setIsLoadingMenu] = useState(false);
    const [summary, setSummary] = useState(null);
    const [isLoadingSummary, setIsLoadingSummary] = useState(false);
    const [showAssignedTo, setShowAssignedTo] = useState(isSlim ? "Company" : "Company");
    const [completionStatus, setCompletionStatus] = useState(isTrue(paramShowAllTasks) || isTrue(window.showAllTasks) ? "All" : "Incomplete");
    const [editingCompletion, setEditingCompletion] = useState(false);
    const [taskView, setTaskView] = useState("table");
    const [searchText, setSearchText] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [boards, setBoards] = useState([]);
    const [boardTasks, setBoardTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [pendingCreate, setPendingCreate] = useState(null);
    const [addTaskParentTaskId, setAddTaskParentTaskId] = useState(null);
    const [selectedCount, setSelectedCount] = useState(0);
    const externalLinkedObjects = paramExternalLinkedObjects || window.externalLinkedObjects || [];

    const loadMenu = (entityType, entityId, { updateTitle = false } = {}) => {
        setIsLoadingMenu(true);
        const argEntityType = entityType === "systematic-review-item" ? "systematic-review" : entityType;
        fetch(`/umbraco/api/TaskManagerApi/Menu?entityType=${encodeURIComponent(argEntityType || "")}&entityId=${encodeURIComponent(entityId || "")}`)
            .then((response) => response.ok ? response.json() : null)
            .then((data) => {
                setFullMenu(data);
                setMenu(data?.Nodes || []);
                setBreadcrumbs(data?.BreadCrumbs || []);
                if (updateTitle && data?.Title)
                    setWhereAmI(data.Title);
            })
            .catch(() => { })
            .finally(() => setIsLoadingMenu(false));
    };

    const loadSummary = (entityType, entityId) => {
        setIsLoadingSummary(true);
        fetch(`/umbraco/api/TaskManagerApi/Summary?entityType=${encodeURIComponent(entityType || "")}&entityId=${encodeURIComponent(entityId || "")}`)
            .then((response) => response.ok ? response.json() : [])
            .then((data) => {
                const me = data.find((item) => item.TaskFor == "Me");
                const company = data.find((item) => item.TaskFor == "Company");
                setSummary({
                    me: { total: me?.Total || 0, late: me?.Late || 0, today: me?.Today || 0, next7days: me?.Next7Days || 0 },
                    company: { total: company?.Total || 0, late: company?.Late || 0, today: company?.Today || 0, next7days: company?.Next7Days || 0 },
                });
            })
            .catch(() => { })
            .finally(() => setIsLoadingSummary(false));
    };

    const loadBoards = (force = false) => {
        if (force)
            invalidateTaskBoardsCache();
        loadTaskBoards()
            .then((data) => setBoards(data || []))
            .catch(() => { });
    };

    const loadBoardTasks = () => {
        const params =
            `search=${encodeURIComponent(appliedSearch)}&sortBy=DueDate&sortType=ASCENDING&page=1&returnAll=true&` +
            `assignedTo=${encodeURIComponent(showAssignedTo)}&completionStatus=${encodeURIComponent(completionStatus)}&` +
            `linkedEntityType=${encodeURIComponent(linkedEntityType || "")}&linkedEntityId=${encodeURIComponent(linkedEntityId || "")}`;
        fetch(`/umbraco/api/TaskManagerApi/TaskList?${params}`)
            .then((response) => response.ok ? response.json() : [])
            .then((data) => setBoardTasks(data || []))
            .catch(() => { });
    };

    useEffect(() => {
        loadMenu(linkedEntityType, linkedEntityId);
        loadSummary(linkedEntityType, linkedEntityId);
        loadBoards();
        fetch("/umbraco/api/TaskManagerApi/Users")
            .then((response) => response.ok ? response.json() : [])
            .then((data) => setUsers(data || []))
            .catch(() => { });
        window.isTaskManagerReady = true;
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setAppliedSearch(searchText), 400);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        if (taskView === "board")
            loadBoardTasks();
    }, [taskView, showAssignedTo, completionStatus, linkedEntityType, linkedEntityId, appliedSearch]);

    useEffect(() => {
        if (!emitter)
            return undefined;
        const onCreate = (data) => {
            // When an appraisal-card Task Manager is mounted, only that instance should
            // handle Assign this Review — not the SR-page Tasks popup instance.
            const appraisalMount = document.querySelector('[data-react-component="task-manager"][data-entity-type="systematic-review-item"]');
            if (appraisalMount && initialEntityType !== "systematic-review-item")
                return;

            setPendingCreate(data || {});
            setTimeout(() => apiRef.current.showAddItemPopup?.(), 0);
        };
        const onReload = () => apiRef.current.refreshData?.();
        emitter.on("createNewTask", onCreate);
        emitter.on("reloadAllTasks", onReload);
        return () => {
            emitter.off("createNewTask", onCreate);
            emitter.off("reloadAllTasks", onReload);
        };
    }, [emitter, initialEntityType]);

    const onMenuSelect = (entityType, entityId, title) => {
        const nextType = entityType || "";
        const nextId = entityId == null || entityId === "" ? "" : entityId;
        setLinkedEntityType(nextType);
        setLinkedEntityId(nextId);
        setWhereAmI(title || "All Tasks");
        setTaskView("table");

        const menuOption = menu.find((item) => item.EntityType == entityType && item.EntityId == entityId);
        const shouldReloadMenu =
            menuOption == null ||
            menuOption.HasChildren ||
            ((entityType == null || entityType === "") && (entityId == null || entityId === ""));

        if (shouldReloadMenu)
            loadMenu(entityType, entityId, { updateTitle: true });

        loadSummary(entityType, entityId);
    };

    const reachedAllowance = isTrue(window.reachedAllowance);
    const hideAdd = isTrue(paramIsReviewApproved) || isTrue(window.isReviewApproved);
    const exportButtonStyle = {
        color: "#203864",
        padding: "7px 20px",
        border: "1px solid #dee2e6",
        borderRadius: "15px",
        backgroundColor: "#f8f9fa",
        fontWeight: "500",
        display: "inline-flex",
        alignItems: "center",
        transition: "all 0.2s ease",
        cursor: "pointer",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        marginRight: "15px",
    };
    const exportDisabledStyle = {
        display: "inline-flex",
        alignItems: "center",
        padding: "7px 20px",
        color: "rgb(49, 49, 73)",
        backgroundColor: "rgb(240, 240, 240)",
        border: "2px solid rgb(204, 204, 204)",
        borderRadius: "15px",
        fontWeight: "500",
        cursor: "help",
        opacity: "0.5",
        marginRight: "15px",
    };

    const assignedToFilter = (
        <div style={{ marginLeft: "20px", display: "flex" }}>
            <span title="Show only my tasks" className={`show-tasks-mine ${showAssignedTo === "Me" ? "" : "show-tasks-mine-unselected"}`} onClick={() => setShowAssignedTo("Me")}><i className="fas fa-user"></i></span>
            <span title="Show everyone's tasks" className={`show-tasks-team ${showAssignedTo === "Company" ? "" : "show-tasks-team-unselected"}`} onClick={() => setShowAssignedTo("Company")}><i className="fas fa-users"></i></span>
        </div>
    );

    const completionFilter = (
        <div style={{ marginLeft: "20px" }}>
            {editingCompletion ? (
                <select autoFocus value={completionStatus} onBlur={() => setEditingCompletion(false)} onChange={(e) => { setCompletionStatus(e.target.value); setEditingCompletion(false); }}>
                    <option value="All">All Tasks</option>
                    <option value="Incomplete">Incomplete Tasks</option>
                    <option value="Completed">Completed Tasks</option>
                </select>
            ) : (
                <span
                    style={{
                        padding: "7px 8px",
                        borderRadius: "5px",
                        border: "1px solid #c0c0c0",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        display: "inline-block",
                    }}
                    onClick={() => setEditingCompletion(true)}
                >
                    {completionStatus === "Completed" ? <i className="fas fa-check-circle" style={{ color: "#00d084" }}></i> : completionStatus === "All" ? <i className="far fa-check-circle"></i> : <i className="far fa-circle"></i>}
                    <span style={{ marginLeft: "5px" }}>{completionStatus === "All" ? "All Tasks" : completionStatus === "Completed" ? "Completed Tasks" : "Incomplete Tasks"}</span>
                </span>
            )}
        </div>
    );

    // Match DataTable's native Add button container (marginRight: 17px) so the
    // right edge lines up with Add/Remove fields on the row below.
    const exportAndAddActions = (
        <div style={{ display: "flex", alignItems: "center", marginRight: "17px", flexShrink: 0 }}>
            {selectedCount > 0 && (
                <span style={{ fontWeight: "bold", marginRight: "10px" }}>
                    {selectedCount} selected item(s)
                </span>
            )}
            {baseStructure.exportUrl && (
                selectedCount > 0 ? (
                    <span
                        className="pointer"
                        style={exportButtonStyle}
                        onClick={() => apiRef.current.openExportPopup?.()}
                    >
                        <i className="fas fa-file-excel" style={{ marginRight: "8px" }}></i>
                        Export
                    </span>
                ) : (
                    <span
                        title="Please select at least one item to export"
                        style={exportDisabledStyle}
                    >
                        <i className="fas fa-file-excel" style={{ marginRight: "8px" }}></i>
                        Export
                    </span>
                )
            )}
            {!hideAdd && (
                reachedAllowance ? (
                    <span className="action-button add-task-button" style={{ backgroundColor: "#c0c0c0" }} title="Allowance limit reached. Contact MedBoard to increase the limit.">
                        <i className="fas fa-plus-circle"></i> Add Task
                    </span>
                ) : (
                    <span className="action-button add-task-button" onClick={() => { setAddTaskParentTaskId(null); apiRef.current.showAddItemPopup?.(); }}>
                        <i className="fas fa-plus-circle"></i> Add Task
                    </span>
                )
            )}
        </div>
    );

    const structure = {
        ...baseStructure,
        hideGrid: taskView !== "table",
        hasLimitedWidth: fillParent,
        // Embeds fill the parent; standalone matches Products/CM (page scroll then table scroll).
        fillParentHeight: fillParent,
        externalLinkedObjects,
        linkedEntityType,
        linkedEntityId,
        pendingCreate,
        addTaskParentTaskId,
        setAddTaskParentTaskId,
        expandable: { countField: "SubtaskCount", parentIdField: "ParentTaskId", expandColumnField: "Name" },
        extraQueryParams: {
            assignedTo: showAssignedTo,
            completionStatus,
            linkedEntityType: linkedEntityType || "",
            linkedEntityId: linkedEntityId || "",
        },
        extraQueryParamsKey: `${showAssignedTo}|${completionStatus}|${linkedEntityType || ""}|${linkedEntityId || ""}`,
        onSelectedReferencesChange: setSelectedCount,
        toolbarHeader: () => exportAndAddActions,
        toolbarAfterSearch: () => (
            <>
                {assignedToFilter}
                {completionFilter}
            </>
        ),
        registerApi: (api) => {
            apiRef.current = { ...apiRef.current, ...api };
        },
    };

    return (
        <div style={{ display: "flex", flexDirection: "row", height: fillParent ? "100%" : "calc(100vh - 74px)", minHeight: 0, overflow: "hidden" }}>
            {showLeftMenu && (
                <TaskMenu
                    menu={menu}
                    fullMenu={fullMenu}
                    isLoading={isLoadingMenu}
                    linkedEntityType={linkedEntityType}
                    linkedEntityId={linkedEntityId}
                    onSelect={onMenuSelect}
                    hideAllTasksNav={!!initialEntityType}
                />
            )}
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    paddingLeft: showLeftMenu ? "10px" : 0,
                    display: "flex",
                    flexDirection: "column",
                    // Standalone: vertical page scroll on the right pane (like Products/CM document scroll).
                    // Embeds: clip to parent and let DataTable fill the remaining height.
                    overflowX: "hidden",
                    overflowY: fillParent ? "hidden" : "auto",
                }}
            >
                <div style={{ flexShrink: 0 }}>
                {!isSlim && (
                    <>
                        {showBreadcrumbs && (
                            <Breadcrumbs
                                breadcrumbs={breadcrumbs}
                                whereAmI={whereAmI}
                                linkedEntityId={linkedEntityId}
                                onSelect={onMenuSelect}
                                hideAllTasksNav={!!initialEntityType}
                            />
                        )}
                        {showWhereAmI && (
                            <h3 style={{ color: "#203864" }}>
                                <EntityTypeIcon entityType={linkedEntityType} />
                                <span style={{ marginLeft: "8px" }}>{whereAmI}</span>
                            </h3>
                        )}
                    </>
                )}
                {showSummaries && <CountSummaries summary={summary} isLoading={isLoadingSummary} />}

                <div style={{ display: "flex", marginBottom: "15px" }}>
                    <div style={{ cursor: "pointer", padding: "0 10px 3px 0", borderBottom: taskView === "table" ? "2px solid #00abff" : "2px solid #f0f0f0" }} onClick={() => setTaskView("table")}>
                        <i className="fas fa-table"></i> Table View
                    </div>
                    <div style={{ cursor: "pointer", padding: "0 10px 3px 5px", borderBottom: taskView !== "table" ? "2px solid #00abff" : "2px solid #f0f0f0" }} onClick={() => setTaskView("board")}>
                        <i className="fas fa-columns"></i> Board View
                    </div>
                    <div style={{ flex: 1, borderBottom: "2px solid #f0f0f0" }}></div>
                </div>

                {taskView !== "table" && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
                    <div className="search-input">
                        <span className="search-icon"><i className="fas fa-search"></i></span>
                        <input placeholder="Search name, description or assignee" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                    </div>
                    {assignedToFilter}
                    {completionFilter}
                    {taskView === "board" && (
                        <span className="action-button board-config-button" style={{ marginLeft: "20px" }} onClick={() => setTaskView("board-configuration")}>
                            <i className="fas fa-cog"></i> Board Configuration
                        </span>
                    )}
                    {taskView === "board-configuration" && (
                        <span className="action-button board-config-button" style={{ marginLeft: "20px" }} onClick={() => setTaskView("board")}>
                            <i className="fas fa-columns"></i> Board View
                        </span>
                    )}
                    <div style={{ flex: 1 }}></div>
                    {!hideAdd && (
                        reachedAllowance ? (
                            <span className="action-button add-task-button" style={{ backgroundColor: "#c0c0c0" }} title="Allowance limit reached. Contact MedBoard to increase the limit.">
                                <i className="fas fa-plus-circle"></i> Add Task
                            </span>
                        ) : (
                            <span className="action-button add-task-button" onClick={() => { setAddTaskParentTaskId(null); apiRef.current.showAddItemPopup?.(); }}>
                                <i className="fas fa-plus-circle"></i> Add Task
                            </span>
                        )
                    )}
                </div>
                )}
                </div>

                <div
                    style={{
                        ...(fillParent
                            ? { flex: 1, minWidth: 0, minHeight: 0, width: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }
                            : { width: "100%", minWidth: 0 }),
                    }}
                >
                    {taskView === "board" && (
                        <BoardView tasks={boardTasks} boards={boards} users={users} onOpenTask={(id) => apiRef.current.loadItemDetails?.(id)} onConfigure={() => setTaskView("board-configuration")} />
                    )}
                    {taskView === "board-configuration" && (
                        <BoardConfiguration boards={boards} onSaved={() => { loadBoards(true); setTaskView("board"); }} onBack={() => setTaskView("board")} />
                    )}

                    <div
                        style={{
                            display: taskView === "table" ? (fillParent ? "flex" : "block") : "none",
                            flexDirection: fillParent ? "column" : undefined,
                            ...(fillParent
                                ? { flex: 1, minWidth: 0, minHeight: 0, width: "100%", overflow: "hidden" }
                                : { width: "100%", minWidth: 0 }),
                        }}
                    >
                        <DataTable
                            dataTableStructure={structure}
                            ItemDetailsPane={ItemDetailsPane}
                            AddItemPopup={AddItemPopup}
                            ReloadStructureFunction={ReloadStructureFunction}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskManagerShell;
